import { Suspense } from 'react';
import { RouteObject, Outlet } from 'react-router';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import AdminRoute from './guards/AdminRoute';
import UserRoute from './guards/UserRoute';
import TestAvailabilityGuard from './guards/TestAvailabilityGuard';
import CheckTestGuard from './guards/CheckTestGuard';
import CustomSpinner from './components/Common/CustomSpinner';
import { withSuspense } from './utils/withSuspense';
import AuthRedirectGuard from './guards/AuthRedirectGuard';
import { RouterErrorBoundary } from './components/ErrorBoundary';
import ErrorPage from './pages/ErrorPage';
import {
  ExamList,
  ExamDetails,
  Home,
  Login,
  QuestionList,
  StartPage,
  ResetPassword,
  OrganizationList,
  StudentList,
  TeacherList,
  NotFound,
  KanjiQuiz,
  ParticleQuiz,
  StudentDetails,
  StudentProfile,
  HiraganaKatakanaQuizMenu,
  AudioQuiz,
  KanaQuiz,
  HiraganaKatakanaPracticeMenu,
  HiraganaPractice,
  KatakanaPractice,
  TestQuizMenu,
  TestQuiz,
  TakeTest,
  ProficiencyTest,
  TakeProficiencyTest,
  CheckTest,
  TakeCheckTest,
  DashboardStartPage,
  Menu,
  DashboardKanjiQuiz,
  DashboardParticleQuiz,
  DashboardHiraganaKatakanaQuizMenu,
  DashboardAudioQuiz,
  DashboardKanaQuiz,
  DashboardHiraganaKatakanaPracticeMenu,
  DashboardHiraganaPractice,
  DashboardKatakanaPractice,
  GrammarQuizMenu,
  VocabularyQuiz,
  DashboardVocabularyQuiz,
  DashboardGrammarQuizMenu,
  PrivacyPolicy,
  TermsOfUsage,
  Top,
  KanjiQuizMenu,
  StageMode,
  DashboardStageMode,
  DashboardKanjiQuizMenu,
  Law,
} from './routes/lazyComponents';
import InitialPassGuard from './guards/InitialPassGuard';
import SuperAdminGuard from './guards/SuperAdminGuard';

// Route configuration
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthProvider>
        <RouterErrorBoundary>
          <SidebarProvider>
            <Suspense fallback={<CustomSpinner height="100vh" />}>
              <Outlet />
            </Suspense>
          </SidebarProvider>
        </RouterErrorBoundary>
      </AuthProvider>
    ),
    children: [
      {
        element: <AuthRedirectGuard />,
        children: [{ path: 'login', element: withSuspense(Login) }],
      },
      { index: true, element: withSuspense(StartPage) },
      { path: 'set_init_pass', element: withSuspense(ResetPassword) },
      { path: 'privacy-policy', element: withSuspense(PrivacyPolicy) },
      { path: 'terms', element: withSuspense(TermsOfUsage) },
      { path: 'law', element: withSuspense(Law) },
      {
        element: <UserRoute />,
        children: [
          {
            element: <InitialPassGuard />,
            children: [
              {
                element: <CheckTestGuard />,
                children: [
                  {
                    element: <TestAvailabilityGuard />,
                    children: [
                      {
                        path: 'home',
                        element: withSuspense(Home),
                      },
                      {
                        path: 'top',
                        element: withSuspense(Top),
                      },
                      {
                        path: 'hiragana-katakana-practice',
                        children: [
                          {
                            index: true,
                            element: withSuspense(HiraganaKatakanaPracticeMenu),
                            handle: { backPath: '/home' },
                          },
                          {
                            path: 'hiragana-practice',
                            element: withSuspense(HiraganaPractice),
                            handle: { backPath: '/hiragana-katakana-practice' },
                          },
                          {
                            path: 'katakana-practice',
                            element: withSuspense(KatakanaPractice),
                            handle: { backPath: '/hiragana-katakana-practice' },
                          },
                        ],
                      },
                      {
                        path: 'hiragana-katakana-quiz',
                        children: [
                          {
                            index: true,
                            element: withSuspense(HiraganaKatakanaQuizMenu),
                            handle: { backPath: '/home' },
                          },
                          {
                            path: 'audio-quiz',
                            element: withSuspense(AudioQuiz),
                            handle: { backPath: '/hiragana-katakana-quiz' },
                          },
                          {
                            path: 'kana-quiz',
                            element: withSuspense(KanaQuiz),
                            handle: { backPath: '/hiragana-katakana-quiz' },
                          },
                        ],
                      },
                      {
                        path: 'grammar-quiz',
                        children: [
                          {
                            index: true,
                            element: withSuspense(GrammarQuizMenu),
                            handle: { backPath: '/home' },
                          },
                          {
                            path: 'particle-quiz',
                            element: withSuspense(ParticleQuiz),
                            handle: { backPath: '/grammar-quiz' },
                          },
                          {
                            path: 'vocabulary-quiz',
                            element: withSuspense(VocabularyQuiz),
                            handle: { backPath: '/grammar-quiz' },
                          },
                        ],
                      },
                      {
                        path: 'kanji-quiz-menu',
                        children: [
                          {
                            index: true,
                            element: withSuspense(KanjiQuizMenu),
                            handle: { backPath: '/home' },
                          },
                          {
                            path: 'kanji-quiz',
                            element: withSuspense(KanjiQuiz),
                            handle: { backPath: '/kanji-quiz-menu' },
                          },
                          {
                            path: 'stage-mode',
                            element: withSuspense(StageMode),
                            handle: { backPath: '/kanji-quiz-menu' },
                          },
                        ],
                      },
                      {
                        path: 'test',
                        children: [
                          {
                            index: true,
                            element: withSuspense(TestQuizMenu),
                            handle: { backPath: '/top' },
                          },
                          {
                            path: 'mini-test',
                            children: [
                              {
                                index: true,
                                element: withSuspense(TestQuiz),
                                handle: { backPath: '/test' },
                              },
                              {
                                path: ':testId',
                                element: withSuspense(TakeTest),
                                handle: { backPath: '/test/mini-test' },
                              },
                            ],
                          },
                          {
                            path: 'proficiency-test',
                            children: [
                              {
                                index: true,
                                element: withSuspense(ProficiencyTest),
                                handle: { backPath: '/test' },
                              },
                              {
                                path: ':testId',
                                element: withSuspense(TakeProficiencyTest),
                                handle: { backPath: '/test/proficiency-test' },
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: 'test/check-test',
                        children: [
                          {
                            index: true,
                            element: withSuspense(CheckTest),
                            handle: { backPath: '/home' },
                          },
                          {
                            path: ':testId',
                            element: withSuspense(TakeCheckTest),
                          },
                        ],
                      },
                      {
                        path: 'profile',
                        element: withSuspense(StudentProfile),
                        handle: { backPath: '/top' },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <SuperAdminGuard />,
        children: [
          {
            path: 'organizations',
            element: withSuspense(OrganizationList),
          },
          {
            path: 'teachers',
            element: withSuspense(TeacherList),
          },
          { path: 'questions', element: withSuspense(QuestionList) },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          {
            element: <InitialPassGuard />,
            children: [
              {
                path: '/',
                children: [
                  {
                    index: true,
                    path: 'students',
                    element: withSuspense(StudentList),
                  },
                  {
                    path: 'students/:studentId',
                    element: withSuspense(StudentDetails),
                  },
                  { path: 'questions', element: withSuspense(QuestionList) },
                  {
                    path: 'quiz',
                    children: [
                      {
                        index: true,
                        element: withSuspense(ExamList),
                      },
                      {
                        path: ':examId/details',
                        children: [
                          {
                            index: true,
                            element: withSuspense(ExamDetails),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'digital-teaching-materials',
                    children: [
                      {
                        index: true,
                        element: withSuspense(DashboardStartPage),
                      },
                      {
                        path: 'menu',
                        element: withSuspense(Menu),
                      },
                      {
                        path: 'hiragana-katakana-practice',
                        children: [
                          {
                            index: true,
                            element: withSuspense(
                              DashboardHiraganaKatakanaPracticeMenu,
                            ),
                          },
                          {
                            path: 'hiragana-practice',
                            element: withSuspense(DashboardHiraganaPractice),
                          },
                          {
                            path: 'katakana-practice',
                            element: withSuspense(DashboardKatakanaPractice),
                          },
                        ],
                      },
                      {
                        path: 'hiragana-katakana-quiz',
                        children: [
                          {
                            index: true,
                            element: withSuspense(
                              DashboardHiraganaKatakanaQuizMenu,
                            ),
                          },
                          {
                            path: 'audio-quiz',
                            element: withSuspense(DashboardAudioQuiz),
                          },
                          {
                            path: 'kana-quiz',
                            element: withSuspense(DashboardKanaQuiz),
                          },
                        ],
                      },
                      {
                        path: 'kanji-quiz-menu',
                        children: [
                          {
                            index: true,
                            element: withSuspense(DashboardKanjiQuizMenu),
                          },
                          {
                            path: 'kanji-quiz',
                            element: withSuspense(DashboardKanjiQuiz),
                          },
                          {
                            path: 'stage-mode',
                            element: withSuspense(DashboardStageMode),
                          },
                        ],
                      },
                      {
                        path: 'grammar-quiz',
                        children: [
                          {
                            index: true,
                            element: withSuspense(DashboardGrammarQuizMenu),
                          },
                          {
                            path: 'particle-quiz',
                            element: withSuspense(DashboardParticleQuiz),
                          },
                          {
                            path: 'vocabulary-quiz',
                            element: withSuspense(DashboardVocabularyQuiz),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'error',
        element: <ErrorPage />,
      },
      { path: '*', element: withSuspense(NotFound) },
    ],
  },
];

export default routes;
